import { useEffect, useRef } from "react";

export type KeepLiveRefreshReason = "initial" | "interval" | "visible";

export interface KeepLiveRefreshContext {
    reason: KeepLiveRefreshReason;
}

export interface KeepLiveRefreshOptions {
    enabled?: boolean;
    intervalMs?: number;
    immediate?: boolean;
    refresh: (context: KeepLiveRefreshContext) => void | Promise<void>;
    onError?: (error: unknown, context: KeepLiveRefreshContext) => void;
}

export const KEEP_LIVE_REFRESH_INTERVAL_MS = 30_000;

export const KEEP_LIVE_COMPONENTS = [
    {
        id: "dashboardCalls",
        label: "Landing page call list",
        intervalMs: KEEP_LIVE_REFRESH_INTERVAL_MS,
        renderRule: "Silent refresh fetches every 30s; new rows render only while the call list is on page 1.",
    },
    {
        id: "todos",
        label: "Todo list",
        intervalMs: KEEP_LIVE_REFRESH_INTERVAL_MS,
        renderRule: "Silent refresh fetches every 30s; new rows render only while the todo list is on page 1.",
    },
] as const;

const isDocumentVisible = () =>
    typeof document === "undefined" || document.visibilityState === "visible";

export const useKeepLiveRefresh = ({
    enabled = true,
    intervalMs = KEEP_LIVE_REFRESH_INTERVAL_MS,
    immediate = false,
    refresh,
    onError,
}: KeepLiveRefreshOptions) => {
    const inFlightRef = useRef(false);
    const refreshRef = useRef(refresh);
    const onErrorRef = useRef(onError);

    useEffect(() => {
        refreshRef.current = refresh;
        onErrorRef.current = onError;
    }, [onError, refresh]);

    useEffect(() => {
        if (!enabled) return;

        let intervalId: ReturnType<typeof setInterval> | null = null;
        let disposed = false;

        const runRefresh = async (reason: KeepLiveRefreshReason) => {
            if (!isDocumentVisible() || inFlightRef.current || disposed) return;

            const context = { reason };
            inFlightRef.current = true;
            try {
                await refreshRef.current(context);
            } catch (error) {
                onErrorRef.current?.(error, context);
            } finally {
                inFlightRef.current = false;
            }
        };

        const startPolling = () => {
            if (intervalId) return;
            intervalId = setInterval(() => {
                void runRefresh("interval");
            }, intervalMs);
        };

        const stopPolling = () => {
            if (!intervalId) return;
            clearInterval(intervalId);
            intervalId = null;
        };

        const handleVisibilityChange = () => {
            if (isDocumentVisible()) {
                startPolling();
                void runRefresh("visible");
            } else {
                stopPolling();
            }
        };

        if (isDocumentVisible()) {
            startPolling();
            if (immediate) {
                void runRefresh("initial");
            }
        }

        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            disposed = true;
            stopPolling();
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, [enabled, immediate, intervalMs]);
};
